import { readonly, ref } from 'vue';
import { collectTemplateFonts, ensureFonts, isServable, onFontsChanged, resolveFont, toCanvasFont } from './fontLoader';

/**
 * Module-level so every consumer (each MLivePreview instance, the stores) shares one counter and
 * one subscription. Increments whenever a face finishes loading; put `void fontsVersion.value`
 * in a computed to make it recompute when late fonts land.
 */
const fontsVersion = ref(0);
let subscribed = false;

/**
 * Thin Vue wrapper over the framework-agnostic `fontLoader`. Gives components a reactive
 * "fonts changed" signal alongside the plain functions.
 */
export function useFontLoader() {
  if (!subscribed) {
    subscribed = true;
    onFontsChanged(() => {
      fontsVersion.value++;
    });
  }

  /**
   * Warm a job's fonts without blocking the caller. Errors are swallowed on purpose: the preview's
   * own font gate does the work later if this never lands.
   */
  const preloadFonts = (families: string[]): void => {
    const started = performance.now();
    void ensureFonts(families)
      .then((result) => {
        // TODO(fonts-logging): temporary diagnostics, remove once preloading is verified in the field.
        console.info(`[fonts] preload finished in ${Math.round(performance.now() - started)}ms`, {
          requested: families,
          loaded: result.loaded,
          missing: result.missing,
          failed: result.failed,
          manifestUnavailable: result.manifestUnavailable,
        });
      })
      .catch((error) => console.warn('[useFontLoader] preload failed', error));
  };

  return {
    fontsVersion: readonly(fontsVersion),
    ensureFonts,
    preloadFonts,
    resolveFont,
    toCanvasFont,
    isServable,
    collectTemplateFonts,
  };
}

/** Test hook: the loader's own reset clears its listeners, so re-subscribe on next use. */
export function __resetUseFontLoaderForTests(): void {
  subscribed = false;
  fontsVersion.value = 0;
}
